import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDragLayer } from 'react-dnd';
import { Icon, Popconfirm, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../../context/complexArtboardContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import PageElementsProvider from '../../../context/pageElementsContext';
import { dummyCallback, DYNAMIC_TABLE_BLOCK } from '../../../utils/complexDocumentConstants';
import { calculateRowSpans, getElementBySelectorAsync } from '../complexDocumentHelper';
import DynamicTableSettings from './DynamicTableSettings';
import ComplexBlockRenderer, { AccessibilitySeparatorTag } from '../ComplexBlockRenderer';
import BlockHighlight from '../BlockHighlight';
import MoveableColumn from './MoveableColumn';
import ResizableColumn from './ResizableColumn';
import DynamicTableColumnHeader from './DynamicTableColumnHeader';
import DynamicTableDragLayer from './DynamicTableDragLayer';
import { CreativeBlockContainer, DynamicTable, CustomTableHeader, CustomTableData,
   MenuListWrapper, MenuListItem } from '../styles';
import { useCancellablePromise } from '../cancellablePromise';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';
import { DYNAMIC_TABLE_OPERATIONS } from './translationValues';

const NAMESPACES = ['DYNAMIC_TABLE_SETTINGS_SIDEBAR'];

const ResizeObserverForGroupHeader  = ({ tableUUID, useStore, resizeCallback, customName = 'DynamicTable' }) => {
 const [] = useStore(`gh-${tableUUID}`, resizeCallback, customName);

 return (<React.Fragment />);
}

const DynamicTableDesign = props => {
  const { itemUUID, parentUUID, editable, getElementStyles, scaling, useStore, onClickBlockSelection= dummyCallback, isInteractiveDesign = false, ...rest } = props;
  const { showVersionSettings } = rest;
  const settingsVisibleRef = useRef(null);
  const tableRef = useRef(null);
  const blockRef = useRef(null);
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [selectedEditor, setSelectedEditor] = useState(null);

  const [extraRow, setExtraRow] = useState([]);
  const overflowInProgress = useRef(false);
  const groupHeaderRef = useRef(null);

  const [groupHeaderHeight, setGroupHeaderHeight] = useState(0);
  const [selectedGroupHeaders, setSelectedGroupHeaders] = useState([]);
  const [groupMenuPosition, setGroupMenuPosition] = useState(null);
  const { t } = useTranslation(NAMESPACES);

  const customSelector = useCallback(store => store[itemUUID], []);
  const [dynamicTableBlock, methods] = useDocumentContext(customSelector, `DynamicTableDesign-${itemUUID}`);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID, []);
  const [currentBlock, updateArtboard] = useArtboardContext(artboardContextSelector, `DynamicTableDesign-${itemUUID}`);

  const customVarSelector = useCallback(store => ({globalVariables: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], relationships: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.RELATIONSHIPS]}), []);
  const [{globalVariables, relationships}] = useDocumentContext(customVarSelector, `DynamicTableDesign-${itemUUID}`);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const checkAccessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY] , []);
  const [isAccessibility] = useArtboardContext(checkAccessibilitySelector, `BlockRenderer-${props.itemUUID}`);

  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `DynamicTableDesign-${itemUUID}`);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(previewModeSelector,  `DynamicTableDesign-${itemUUID}`);

  const currentDesignSelector = useCallback(
    store =>
      [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]),
    [],
  );
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, 'CreativeBlockDesign');

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `TextBlock-${itemUUID}`);

  const blockSelected = selectedBlocks.includes(itemUUID);
  const updateGlobalVariables = methods.addNewVariable;

  const { addNewCancellablePromise, removePendingPromise, clearPendingPromises } = useCancellablePromise();

  const updateSidebar = useCallback(() => {
    updateArtboard({
      [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
      [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: DYNAMIC_TABLE_BLOCK,
      [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: true,
      [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
      [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
      [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false
    });
    setSelectedHeader(null)
  }, [itemUUID, parentUUID]);

  const onClose = useCallback(() => {
    updateArtboard({ [CONTEXT_KEYS.CURRENT_BLOCK]: null, [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false,
      [CONTEXT_KEYS.CURRENT_PAGE]: undefined, [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: '' });
  }, []);

  useEffect(() => {
    settingsVisibleRef.current = currentBlock;
  }, [currentBlock]);

  const isRearranging = useDragLayer(monitor => monitor.getItemType() === 'column' && monitor.isDragging());

  const onTableOverflow = useCallback((e) => {
    const { pageId, overflowBlock, overflowPageId } = e.detail;

    if (overflowInProgress.current) return;

    const currentPage = document.getElementById(`complex-page-${pageId}`);
    const currentPageRect = currentPage.getBoundingClientRect();
    const maxHeight = currentPageRect.top + parseInt(currentPage.style.minHeight.replaceAll('px', ''));
    const blockDOM = tableRef.current.parentElement;
    const blockRect = blockDOM.getBoundingClientRect();

    const verticalPadding = parseInt(blockDOM.style.paddingTop.replaceAll('px','')) + parseInt(blockDOM.style.paddingBottom.replaceAll('px',''));
    const verticalPaddingBottom = parseInt(blockDOM.style.paddingBottom.replaceAll('px',''));
    // const verticalPaddingBottom = 0;
    // TODO : Check if topBlock fits within the bottom boundary
    const topBlockDOM = blockDOM.getAttribute('data-top-block') !== 'undefined' ? document.getElementById(blockDOM.getAttribute('data-top-block')) : undefined;
    if (topBlockDOM) {
      const topBlockReact =  topBlockDOM.getBoundingClientRect();
      if (topBlockDOM.bottom + verticalPaddingBottom > maxHeight) {
        methods.removeTableFromParentPage(parentUUID, pageId, itemUUID, overflowPageId, overflowBlock);
        return;
      }
    }

    const groupHeaderDOM = document.getElementById(`gh-${itemUUID}`);
    if (groupHeaderDOM) {
      const groupHeaderRect = groupHeaderDOM.getBoundingClientRect();
      if (groupHeaderRect.bottom + verticalPaddingBottom > maxHeight) {
        methods.removeTableFromParentPage(parentUUID, pageId, itemUUID, overflowPageId, overflowBlock);
        return;
      }
    }

    // TODO : check if table header fits within the bottom boundary
    const tableHeaderDOM = document.getElementById(`th-${itemUUID}`);
    const tableHeaderRect = tableHeaderDOM ? tableHeaderDOM.getBoundingClientRect() : undefined;
    const tableWithinBottom = tableHeaderRect
      ? tableHeaderRect.bottom + verticalPaddingBottom < maxHeight
      : true;
    if (tableWithinBottom) {
    // TODO : yes, continue
        let overflowingRowIndex;
        let partial = false;
        // TODO : Check if first row fits within the bottom boundary
        const tableFirstRowDOM = document.getElementById(`tr-${itemUUID}-0`);
        const tableFirstRowRect = tableFirstRowDOM.getBoundingClientRect();
        console.log("tableFirstRowRect ", tableFirstRowDOM, tableFirstRowRect);
        if (tableFirstRowRect.bottom + verticalPaddingBottom < maxHeight) {
          console.log("Coming here 1 ", tableFirstRowRect.bottom + verticalPaddingBottom, maxHeight, tableRef.current);
        // TODO : yes, continue
          // TODO : Iterate over each row to check if it fits the bottom boundary
          let rowStartIndex = 2;
          if (groupHeaderDOM) {
            rowStartIndex += 1;
          }
          if (tableHeaderRect) {
            rowStartIndex += 1;
          }
          const tableRows = Array.from(tableRef.current.children).slice(rowStartIndex);
          console.log({ tableRows });
          let i = 0;
          while (i < tableRows.length) { // TODO : If row fits, continue
            let rowRect = tableRows[i].getBoundingClientRect();
            if (rowRect.bottom + verticalPaddingBottom > maxHeight) {
              overflowingRowIndex = i;
              break;
            }
            i++;
          }
          console.log({ rowStartIndex, overflowingRowIndex });
          if (overflowingRowIndex !== undefined) {
            // TODO : Check if row partially fits the bottom boundary - atleast first line
            const heightOfLine = tableRows[overflowingRowIndex].children[0].children[0].children[0].getBoundingClientRect().bottom; // Targetting first word
            console.log("heightOfLine :: ", tableRows[overflowingRowIndex].children[0].children[0], heightOfLine, verticalPaddingBottom, maxHeight);
            if (heightOfLine + verticalPaddingBottom < maxHeight) {
              // TODO : yes, delegate split operation to text block that does not fit - cell content is not a quill renderer :(
              partial = true;
            } else {

            }
            overflowingRowIndex += 1;
          } else {
            // TODO : Check if bottom block fits, then intentionally move last row to next page
            const bottomBlock = blockDOM.getAttribute('data-bottom-block') !== 'undefined' ? document.getElementById(blockDOM.getAttribute('data-bottom-block')) : undefined;
            if (bottomBlock) {
              const bottomBlockHeight = bottomBlock.getBoundingClientRect();
              if (bottomBlockHeight.bottom + verticalPaddingBottom > maxHeight) {
                overflowingRowIndex = tableRows.length;
              }
            }
          }
        } else { // TODO : no
          console.log("Coming here 2 ", tableFirstRowRect.bottom + verticalPaddingBottom, maxHeight);
          if (tableFirstRowDOM.children[0].children[0].children[0]) {
            // TODO : Check if first row partially fits the bottom boundary - atleast first line
            console.log("First child :: ", tableFirstRowDOM.children[0].children[0].children[0].cloneNode(true));
            const heightOfFirstLine = tableFirstRowDOM.children[0].children[0].children[0]?.getBoundingClientRect().bottom; // Targetting first word
            console.log("heightOfFirstLine ", heightOfFirstLine, verticalPaddingBottom, maxHeight);
            if (heightOfFirstLine + verticalPaddingBottom < maxHeight) {
              // TODO : yes, delegate split operation to text block that does not fit - cell content is not a quill renderer :(
              overflowingRowIndex = 0;
              partial = true;
            } else { // TODO : no, move table block to overflow page, removing the table from current page
              methods.removeTableFromParentPage(parentUUID, pageId, itemUUID, overflowPageId, overflowBlock);
              return;
            }
          }
        }
        if (overflowingRowIndex !== undefined && partial) { // TODO : If row does not fit
          let tableRowDOM = tableRef.current.children[overflowingRowIndex + 1];

          if (overflowingRowIndex + 1 < tableRef.current.children.length - 1) {
            overflowInProgress.current = true;
            methods.moveRowsToOverflowingTable(itemUUID, overflowBlock, overflowingRowIndex + 1, overflowPageId, parentUUID);
          }

          console.log("Overflowing 1 ", overflowingRowIndex);
          let overflowRowContents = [];

          let cellIndex = 0;
          while (cellIndex < tableRowDOM.children.length) {
            const parentSpan = tableRowDOM.children[cellIndex].children[0];
            const totalWords = parentSpan.children.length;

            console.log({ cellIndex, totalWords, copy: parentSpan.cloneNode(true) });

            let leftLength = 0, rightLength = totalWords, median = leftLength;
            while (median >= 0 && leftLength < rightLength) {
              median = Math.round((leftLength + rightLength) / 2);

              if (median >= totalWords) break;

              let previousBounds = median - 1 > 0
                ? parentSpan.children[median - 1].getBoundingClientRect()
                : undefined;
              let bounds = parentSpan.children[median].getBoundingClientRect();
              let nextBounds = median + 1 < totalWords
                ? parentSpan.children[median + 1].getBoundingClientRect()
                : undefined;

              console.log("First while bounds :: ", leftLength, rightLength, median);

              if (bounds.bottom + verticalPaddingBottom < maxHeight) {
                if (nextBounds && nextBounds.bottom + verticalPaddingBottom > maxHeight) {
                  median = median + 1;
                  break;
                } else {
                  leftLength = median;
                }
              } else {
                if (previousBounds && previousBounds.bottom + verticalPaddingBottom < maxHeight) {
                  break;
                } else {
                  rightLength = median;
                }
              }

              console.log("NExt iter ::: ", median);
            }
            median = Math.round(median);
            if (median >= totalWords) {
              console.log("Cell does not overflow!!", leftLength, rightLength, median);
              // overflowRowContents.push(undefined);
            } else {
              // Split content that does not fit
              const rowClone = parentSpan.cloneNode(true);
              let overflowContents = Array.from(rowClone.children).slice(median).map(spanEl => spanEl.firstChild.textContent).join("");
              console.log("After first while loop ", median, rowClone, overflowContents);
              parentSpan.replaceChildren(...Array.from(rowClone.children).slice(0, median));
              overflowRowContents.push(overflowContents);
            }
            cellIndex++;
          }

          console.log({ overflowRowContents, table: `dynamic-table-${overflowBlock.uuid}` });
          if (overflowRowContents.length > 0) {
          // TODO : push overflowing partial content and rest of the rows to the overflowing page
          getElementBySelectorAsync(`#dynamic-table-${overflowBlock.uuid}`).then((el) => {
            console.log("Overflow table element :: ", el);
            el.dispatchEvent(new CustomEvent('addTopRow', { detail: { overflowRowContents } }));
            if (overflowInProgress.current === true) {
              tableRef.current.replaceChildren(...Array.from(tableRef.current.children).slice(0, overflowingRowIndex + 2));
              overflowInProgress.current = false;
            }
          });
          }
        } else if (overflowingRowIndex !== undefined && !partial) {
          console.log("Overflowing 2 ", overflowingRowIndex);
          // TODO : push this row and rest of the rows to the overflowing page
          methods.moveRowsToOverflowingTable(itemUUID, overflowBlock, overflowingRowIndex, overflowPageId, parentUUID);
        } else {
          console.log("Not overflowing!!");
          // The table actually fits!!! can this trigger resize observer?
        }
    } else { // TODO : no, move table block to overflow page, removing the table from current page
      console.log("Coming here 1");
      methods.removeTableFromParentPage(parentUUID, pageId, itemUUID, overflowPageId, overflowBlock);
    }
  }, [itemUUID, parentUUID]);

  const addTopRow = useCallback((e) => {
    const { overflowRowContents } = e.detail;
    console.log("overflowRowContents :: ", overflowRowContents);
    setExtraRow(overflowRowContents);
  }, []);

  useEffect(() => {
    if (pageOverflowEnabled) {
      tableRef.current.addEventListener('tableOverflow', onTableOverflow);
      tableRef.current.addEventListener('addTopRow', addTopRow);
    }
  }, [pageOverflowEnabled, onTableOverflow])

  useEffect(() => {
    return () => {
      if (settingsVisibleRef.current) {
        updateArtboard({ [CONTEXT_KEYS.CURRENT_BLOCK]: null, [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false,
          [CONTEXT_KEYS.CURRENT_PAGE]: undefined, [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: '' });
      }
    }
  }, []);

  useEffect(() => {
    if (!dynamicTableBlock.style.tableProperties?.showGroupHeaderContent) {
      setGroupHeaderHeight(0);
    }
  }, [dynamicTableBlock]);

  /*const onHeaderOptionsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const headerIndex = parseInt(e.currentTarget.id.replaceAll(`t-options-${itemUUID}-`, ''), 10);
    setSelectedHeader(headerIndex);
  }, []);*/

  const onRemoveColumn = useCallback((e, headerIndex) => {
    e.preventDefault();
    e.stopPropagation();
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'delete',
         entity: 'column',
         headerIndex
       },
       itemUUID,
       flowUUID: parentUUID
    }, true, globalVariables);
    setSelectedHeader(null);
    setSelectedEditor(null);
  }, [itemUUID, parentUUID, globalVariables]);

  const resizeCallback = useCallback((entry) => {
    if (!entry) {
      return;
    }
    const contentSize = entry.contentBoxSize[0];
    console.log("Resize callback called in group header!! ", contentSize.blockSize);
    setGroupHeaderHeight(contentSize.blockSize);
  }, []);

  const onRearrange = useCallback((dragIndex, dropIndex) => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'rearrange',
         entity: 'column',
         dragIndex,
         dropIndex,
       },
       itemUUID,
       flowUUID: parentUUID,
    });
    setSelectedHeader(null);
    setSelectedEditor(null);
  }, [itemUUID, parentUUID]);

  const onResize = useCallback((uuid, columnIndex, differenceInWidth) => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'resize',
         entity: 'column',
         columnIndex,
         differenceInWidth,
       },
       itemUUID,
       flowUUID: parentUUID,
    });
  }, [itemUUID, parentUUID]);

  const onAddColumn = useCallback((e) => {
    let headerIndex = e.currentTarget.id.replaceAll(`add-col-${itemUUID}-`, '');
    const isLeft = headerIndex === 'first';

    if (!isLeft) {
      headerIndex = parseInt(headerIndex, 10);
    }

    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
         op: 'add',
         entity: 'column',
         headerIndex: isLeft ? 0 : headerIndex,
         isLeft
       },
       itemUUID,
       flowUUID: parentUUID
    }, true, globalVariables);
    setSelectedHeader(null);
    setSelectedEditor(null);
  }, [itemUUID, parentUUID, globalVariables]);

  const toggleSerialNumColumn = () => {
    const isSerialized = dynamicTableBlock?.contents[0]?.content?.contents[0]?.style?.isSerialNumber;
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: !isSerialized ? 'show-serial-number' : 'delete',
        isSerialized,
        entity: 'column',
        headerIndex: 0
      },
      itemUUID,
      flowUUID: parentUUID,
    });
  }

  /*const closeHeaderOptions = useCallback((e) => {
    if (selectedHeader === null) return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedHeader(null);
    setSelectedEditor(null);
  }, [selectedHeader]);*/

  const onSingleClick = useCallback(async (e, index = undefined) => {
    const headerIndex = index !== undefined ? index : parseInt(e.currentTarget.id.replaceAll(`t-column-${itemUUID}-`, ''), 10);
    clearPendingPromises();
    const promise1 = addNewCancellablePromise(300);
    try {
      await promise1.promise;
      removePendingPromise(promise1);
      setSelectedHeader(selectedHeader => {
         console.log({ headerIndex, selectedHeader });
         if (selectedHeader !== headerIndex) {
           setSelectedEditor(null);
           return headerIndex;
         } else {
           setSelectedEditor(null);
           return null;
         }
         return selectedHeader;
      });
    } catch (err) {
      removePendingPromise(promise1);
      console.error(err);
      if (!e.isCancelled) {
        throw e.error;
      }
    }
  }, [itemUUID]);

  const onEditorDoubleClick = useCallback((editor) => {
    clearPendingPromises();
    setSelectedEditor(editor);
  }, [itemUUID]);

  const onClickGroupHeader = useCallback((headerIndex) => {
    setSelectedEditor(null);
    setSelectedHeader(null);
    // TODO : Set selection of group headers
    if (groupMenuPosition !== null) {
      setGroupMenuPosition(null);
      setSelectedGroupHeaders([]);
      return;
    }
    setSelectedGroupHeaders(existingList => {
      if (existingList.length > 0) {
        if (existingList.includes(headerIndex)) {
          const remainingList = existingList.filter(col => col !== headerIndex);
          if (remainingList.length === 1) {
            return remainingList;
          } else {
            return [remainingList[0]];
          }
        }
        if (headerIndex - existingList[existingList.length - 1] === 1) { // Add to right
          return [...existingList, headerIndex];
        } else if (existingList[0] - headerIndex === 1) { // Add to left
          return [headerIndex, ...existingList];
        } else { // Replace
          return [headerIndex];
        }
      }
      return [headerIndex];
    })
  }, [groupMenuPosition]);

  const onGroupHeaderRightClick = useCallback((e) => {
    if (selectedGroupHeaders.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setGroupMenuPosition({ x: e.nativeEvent.clientX, y: e.nativeEvent.clientY });
    }
  }, [selectedGroupHeaders]);

  const mergeCells = useCallback(() => {
    if (selectedGroupHeaders.length <= 1) return;
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: 'merge',
        entity: 'groupHeader',
        startIndex: selectedGroupHeaders[0],
        mergeLength: selectedGroupHeaders.length
      },
      itemUUID,
      flowUUID: parentUUID,
    });
    setSelectedGroupHeaders([]);
    setGroupMenuPosition(null);
  }, [selectedGroupHeaders, itemUUID, parentUUID]);

  const splitCells = useCallback(() => {
    methods.updateBlock('table-operations', itemUUID, {
      'table-operations': {
        op: 'split',
        entity: 'groupHeader',
        startIndex: selectedGroupHeaders[0],
        mergeLength: selectedGroupHeaders.length
      },
      itemUUID,
      flowUUID: parentUUID,
    });
    setSelectedGroupHeaders([]);
    setGroupMenuPosition(null);
  }, [selectedGroupHeaders, itemUUID, parentUUID]);

  const headers = dynamicTableBlock.contents;
  const { totalRows, columnLevels, tableContents, isNoDataSet=false, emptyText=[], levels } = useMemo(() => {
    const { repeatBlockName, repeatBlockIndex, variableContract, contents, style: {tableProperties:{emptyText}} } =
      dynamicTableBlock || {};

    const baseName = variableContract?.[0]?.name;
    const contentName = variableContract?.[0]?.content?.[0]?.name;

    const updatedDataSet =
      repeatBlockName && dataSet ? dataSet[repeatBlockName]?.[repeatBlockIndex] : dataSet;

    const tableName = repeatBlockName ? contentName : baseName;

    const tableInfo = updatedDataSet?.[tableName];
    
    const isNoDataSet = !Array.isArray(tableInfo) || tableInfo.length === 0 || tableInfo.every(o => !Object.keys(o || {}).length);

    const { totalRows, columnLevels, tableContents, levels } = calculateRowSpans(
      contents,
      updatedDataSet,
      tableName
    );
    return { totalRows, columnLevels, tableContents, isNoDataSet, emptyText, levels };
  }, [
    dataSet,
    dynamicTableBlock.repeatBlockName,
    dynamicTableBlock.repeatBlockIndex,
    dynamicTableBlock.variableContract,
    dynamicTableBlock.contents,
  ]);

  const updateDynamicTableContents = useCallback((contents) => {
    methods.updateBlock('contents', itemUUID, { contents, flowUUID: parentUUID, type: DYNAMIC_TABLE_BLOCK }, true, globalVariables);
  }, [itemUUID, parentUUID]);

  let leftStart = 0;

  /*let tableContents = dataSet ? dataSet[dynamicTableBlock.variableContract[0]?.name] : [];

  if (pageOverflowEnabled && dynamicTableBlock.overflowingTable) {
    if (dynamicTableBlock.overflowingDataIndex === undefined) {
      tableContents = [];
    } else {
      tableContents = tableContents.slice(
        dynamicTableBlock.overflowingDataIndex || 0,
        dynamicTableBlock.overflowingDataLength
          ? (dynamicTableBlock.overflowingDataIndex || 0) + dynamicTableBlock.overflowingDataLength
          : tableContents.length
        );
    }
  } else if (pageOverflowEnabled && dynamicTableBlock.overflowingDataLength) {
    tableContents = tableContents.slice(0, dynamicTableBlock.overflowingDataLength);
  }

  if (pageOverflowEnabled && dataSet && extraRow.length > 0) {
    let extraDataset = headers.reduce((acc, columnDef, headerIndex) => {
      let mappedVariableId = parseInt(columnDef.content.contents[0]?.content?.replaceAll('{', '')?.replaceAll('}', ''));
      if (!isNaN(mappedVariableId)) {
        let mappedVariable = globalVariables.find(variable => variable.id === mappedVariableId);
        const obj = columnDef.content?.contents?.length > 0 ? {
          [mappedVariable] : extraRow[headerIndex]
        } : {};
        return {...acc, ...obj};
      }
      return acc;
    }, {});
    console.log({ extraDataset, tableContents })
    tableContents = [extraDataset, ...tableContents];
  }*/

  const tableHeaderVisible = dynamicTableBlock.overflowingTable
    ? dynamicTableBlock.style.tableProperties?.showHeader && dynamicTableBlock.style.tableProperties?.showHeaderOnAllPages
    : dynamicTableBlock.style.tableProperties?.showHeader;

  const tableGroupHeaderVisible = dynamicTableBlock.overflowingTable
    ? dynamicTableBlock.style.tableProperties?.showGroupHeaderContent && dynamicTableBlock.style.tableProperties?.showHeaderOnAllPages
    : dynamicTableBlock.style.tableProperties?.showGroupHeaderContent;

  const topBlockVisible = dynamicTableBlock.overflowingTable
    ? dynamicTableBlock.style.tableProperties?.showTopBlock && dynamicTableBlock.style.tableProperties?.showtopBlockOnAllPages
    : dynamicTableBlock.style.tableProperties?.showTopBlock;

  const bottomBlockVisible = pageOverflowEnabled
    ? dynamicTableBlock.overflowingDataLength === undefined ? dynamicTableBlock.style.tableProperties?.showBottomBlock : false
    : dynamicTableBlock.style.tableProperties?.showBottomBlock;

  const containerRect = blockRef.current ? blockRef.current.getBoundingClientRect() : null;
  // const containerRect = blockRef.current ? { top: blockRef.current.offsetTop, left: blockRef.current.offsetLeft } : null;
  const isSerialized = dynamicTableBlock?.contents[0]?.content?.contents[0]?.style?.isSerialNumber

  return (
    <>
      <div style={{ background: blockSelected && '#ACCEF7' }}>
        {topBlockVisible && (
          <ComplexBlockRenderer
            key={dynamicTableBlock.style.tableProperties?.topBlock}
            itemUUID={dynamicTableBlock.style.tableProperties?.topBlock}
            editable={editable}
            parentUUID={parentUUID}
            getElementStyles={getElementStyles}
            scaling={scaling}
            {...rest}
            onClickBlockSelection= {() => onClickBlockSelection({itemUUID, parentUUID}) }
          />
        )}
        <CreativeBlockContainer
          id={itemUUID}
          currentBlock={currentBlock}
          data-top-block={dynamicTableBlock.style.tableProperties?.topBlock}
          data-bottom-block={dynamicTableBlock.style.tableProperties?.bottomBlock}
          style={{
            background: dynamicTableBlock.style.backgroundColor,
            overflow: 'visible',
            paddingLeft: dynamicTableBlock.style.padding.left,
            paddingTop: dynamicTableBlock.style.padding.top,
            paddingBottom: dynamicTableBlock.style.padding.bottom,
            paddingRight: dynamicTableBlock.style.padding.right,
          }}
          ref={blockRef}
          onDoubleClick={editable ? updateSidebar : dummyCallback}
          onClick={editable && isDocumentOrFlowDesign && !currentBlock ? (e) => {
            if (e.ctrlKey || e.metaKey) {
             onClickBlockSelection({itemUUID, parentUUID})
            }
          } : undefined}
          onMouseMove={(e) => (editable || isInteractiveDesign || showVersionSettings) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)}
        >
          {currentBlock && dynamicTableBlock.style.backgroundColor === undefined && (
            <BlockHighlight pageUUID={parentUUID} />
          )}
          <DynamicTable ref={tableRef} id={`dynamic-table-${itemUUID}`} border={dynamicTableBlock.style.border}>
            <PageElementsProvider isEmail={true} isComplexDesign={true} parentElements={headers} setParentElements={updateDynamicTableContents}>
            <colgroup>
              {headers.map((header, headerIndex) => {
                return (
                  <col style={{ width: `${header.headerContent.style.width}%`}} />
                )
              })}
            </colgroup>
            {tableGroupHeaderVisible && (
            <tr id={`gh-${itemUUID}`} ref={groupHeaderRef} onContextMenu={onGroupHeaderRightClick}>
              {headers.filter(header => header.groupHeaderContent).map((header, headerIndex) => {
                return (
                  <CustomTableHeader key={header.uuid} id={`gh-${header.uuid}`} groupHeader={true} customStyle={header.groupHeaderContent?.style} colSpan={header.groupHeaderContent?.columnSpan || 1}>
                    <div
                      className="tableHeader"
                    >
                      <DynamicTableColumnHeader
                        selected={currentBlock}
                        headerContent={header.groupHeaderContent}
                        uuid={`gh-text-${header.uuid}`}
                        onClick={() => onClickGroupHeader(headerIndex)}
                        scaling={scaling}
                        editable={editable}
                        headerSelected={selectedEditor === `gh-text-${header.uuid}`}
                        setSelectedEditor={setSelectedEditor}
                        cellSelected={selectedGroupHeaders.includes(headerIndex)}
                        customClassName={currentBlock && selectedEditor !== `gh-text-${header.uuid}` ? 'groupHeader' : ''}
                      />
                    </div>
                  </CustomTableHeader>
                )
              })}
            </tr>
            )}
            {tableHeaderVisible && (
            <tr id={`th-${itemUUID}`}>
              {headers.map((header, headerIndex) => {
                return (
                  <CustomTableHeader key={header.uuid} id={`th-${header.uuid}`} customStyle={header.headerContent.style}>
                    <div
                      className="tableHeader"
                    >
                      <DynamicTableColumnHeader
                        selected={currentBlock}
                        headerContent={header.headerContent}
                        uuid={header.uuid}
                        onClick={(e) => onSingleClick(e, headerIndex)}
                        scaling={scaling}
                        editable={editable}
                        headerSelected={selectedEditor === header.uuid}
                        setSelectedEditor={onEditorDoubleClick}
                      />
                    </div>
                  </CustomTableHeader>
                )
              })}
            </tr>
            )}
            {/*tableContents.map((row, rowIndex) => (
            <tr id={`tr-${itemUUID}-${rowIndex}`}>
              {headers.map((column, headerIndex) => {
                const mappedVariableId =  column.content?.contents?.length > 0 ? parseInt(column.content?.contents[0].content.replaceAll("{", "").replaceAll("}", "")) : NaN;
                let mappedVariable = '';
                let mappedVariableDetails;
                if (!isNaN(mappedVariableId)) {
                  mappedVariableDetails = globalVariables.find(variable => variable.id === mappedVariableId);
                  mappedVariable = mappedVariableDetails.placeHolderText;
                  // console.log("mappedVariableDetails :: ", mappedVariable, row[mappedVariable]);
                }
                const cellFormatting = column.content.contents[0]?.style;
                const cellStyles = column.content.style;
                const cellPadding = `${cellStyles.padding.top}px ${cellStyles.padding.right}px ${cellStyles.padding.bottom}px ${cellStyles.padding.left}px`;
                const words = pageOverflowEnabled ? row[mappedVariable]?.split(' ') || [] : [];
                return (
                  <CustomTableData
                    key={column.uuid}
                    id={`tr-${column.uuid}`}
                    customStyle={cellStyles}
                    style={{
                      padding: cellPadding,
                      height: mappedVariable !== '' ? 'auto' : 20,
                      width: `${column.headerContent.style.width}%`,
                      textAlign: column.content.contents[1]?.style?.alignment || 'left',
                    }}
                  >
                    <span style={{
                      fontFamily: cellFormatting?.fontFamily || documentStyle?.fontFamily,
                      fontSize: cellFormatting?.fontSize || parseInt(documentStyle?.fontSize),
                      color: cellFormatting?.color || 'black',
                      backgroundColor: cellFormatting?.backgroundColor || 'transparent',
                      fontWeight: cellFormatting?.fontWeight?.includes('bold') ? 'bold' : 'normal',
                      fontStyle: cellFormatting?.fontWeight?.includes('italics') ? 'italic' : 'normal',
                      textDecoration: cellFormatting?.fontWeight?.includes('underline') ? 'underline' : 'none',
                      lineHeight: column.content.contents[1]?.style?.lineSpacing || '1.5em',
                      letterSpacing: cellFormatting?.letterSpacing || 'normal',
                      display: 'block'
                    }}>
                      {pageOverflowEnabled
                      ? words.map(
                        (word, index) => {
                          return (
                            <span data-word-index={index}>{`${word}${index < words.length - 1 ? ' ' : ''}`}</span>
                          )
                        }
                      )
                      : row[mappedVariable]}
                    </span>
                    {isAccessibility && (
                      <AccessibilitySeparatorTag
                        accessibleInfo={{ accessibleType: 'dynamicTable', level: null }}
                      />
                    )}
                  </CustomTableData>
                )
              })}
            </tr>))*/}
            {dataSet && tableContents.map((row, r) => (
              <tr key={`tr-${itemUUID}-${r}`} id={`tr-${itemUUID}-${r}`}>
                {row.map((col, cIdx) => {
                  return (
                    <CustomTableHeader
                      key={`${col.uuid}-${r}-${cIdx}`}
                      id={`th-${col.uuid}-${r}-${cIdx}`}
                      customStyle={col.content.style}
                      rowSpan={col.rowSpan}
                      colSpan={col.colSpan || 1}
                    >
                      <div className="tableHeader">
                        <DynamicTableColumnHeader
                          selected={false}
                          headerContent={ isNoDataSet ? {...col.content, contents: emptyText} : col.content}
                          uuid={`${col.uuid}-row-${r}-${cIdx}`}
                          relationships={relationships}
                          onClick={dummyCallback}
                          scaling={scaling}
                          editable={false}
                          headerSelected={false}
                          setSelectedEditor={dummyCallback}
                          isFirstRow={false}
                          dataSet={col.dataSet}
                          levels={levels}
                          isSerialized={isSerialized}
                          rowSpan={col.rowSpan}
                        />
                      </div>
                    </CustomTableHeader>
                  );
                })}
              </tr>
            ))}
            {!dataSet && Array.from({ length: totalRows }, (_, r) => (
              <tr key={`tr-${itemUUID}-${r}`} id={`tr-${itemUUID}-${r}`}>
                {columnLevels.map((col, cIdx) => {
                  if (r % col.rowSpan !== 0 || (isSerialized && cIdx === 0 && r !== 0)) return null;

                  const header = headers[cIdx];
                  const isFirstRow = r === 0;
                  const isCellEditable = r === 0 && (!isSerialized || cIdx !== 0);

                  return (
                    <CustomTableHeader
                      key={`${header.uuid}-${r}-${cIdx}`}
                      id={`th-${header.uuid}-${r}-${cIdx}`}
                      customStyle={header.content.style}
                      rowSpan={isSerialized && isFirstRow && cIdx === 0 ? totalRows : col.rowSpan}
                    >
                      <div className="tableHeader">
                        <DynamicTableColumnHeader
                          selected={isFirstRow ? currentBlock : false}
                          headerContent={header.content}
                          uuid={
                            isFirstRow
                              ? `${header.uuid}-row`
                              : `${header.uuid}-row-${r}-${cIdx}`
                          }
                          relationships={relationships}
                          onClick={(e) => onSingleClick(e, cIdx)}
                          scaling={scaling}
                          editable={isCellEditable ? editable : false}
                          headerSelected={selectedEditor === `${header.uuid}-row`}
                          setSelectedEditor={onEditorDoubleClick}
                          isFirstRow={isFirstRow}
                          levels={levels}
                        />
                      </div>
                    </CustomTableHeader>
                  );
                })}
              </tr>
            ))}

            {currentBlock && headers.map((column, columnIndex) => {
              leftStart += column.headerContent.style.width;
              return (
                <>
                {columnIndex === 0 && (
                  <ResizableColumn
                    itemUUID={itemUUID}
                    uuid={column.uuid}
                    columnIndex={"first"}
                    isResizable={false}
                    onResize={onResize}
                    left={0}
                    leftStart={0}
                    scaling={scaling}
                    onAddColumn={onAddColumn}
                  />
                )}
                <MoveableColumn
                  canMove={selectedHeader === columnIndex}
                  columnIndex={columnIndex}
                  uuid={column.uuid}
                  tableUUID={itemUUID}
                  columnWidth={column.headerContent.style.width}
                  customStyle={{
                    // width: selectedHeader !== columnIndex && isRearranging ? 0 : column.headerContent.style.width - 4,
                    width: `calc(${column.headerContent.style.width}% - 4px)`,
                    pointerEvents: selectedHeader === columnIndex ? 'all' : 'none'
                  }}
                  left={4}
                  groupHeaderHeight={groupHeaderHeight}
                  leftStart={leftStart - column.headerContent.style.width}
                  onSingleClick={onSingleClick}
                  /*onDoubleClick={() => {
                    console.log("Double click enabled!!");
                    clearPendingPromises();
                    // setSelectedEditor(column.uuid);
                  }}*/
                  tableRef={tableRef}
                  scaling={scaling}
                  onRearrange={onRearrange}
                  selected={currentBlock}
                >
                  {selectedHeader === columnIndex && !isRearranging && (<div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: "#00c4cc",
                    opacity: 0.2
                  }} />)}
                  {currentBlock && (columnIndex !==0 && (dynamicTableBlock?.contents[0]?.content?.contents[0]?.style?.isSerialNumber ? columnIndex > 1 : true)) && (
                    <>
                      <div className={`headerOptions hoverButton`} id={`t-options-${itemUUID}-${columnIndex}`}>
                        <Tooltip
                          title={t(DYNAMIC_TABLE_OPERATIONS.REMOVE_COLUMN.key, DYNAMIC_TABLE_OPERATIONS.REMOVE_COLUMN.value)}
                        >
                          <Popconfirm
                              title={t(DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_MESSAGE.key, DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_MESSAGE.value)}
                              okText={t(DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_YES.key, DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_YES.value)}
                              cancelText={t(DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_NO.key, DYNAMIC_TABLE_OPERATIONS.DELETE_CONFIRMATION_NO.value)}
                              onConfirm={e => onRemoveColumn(e, columnIndex)}
                              overlayStyle={{ width:300 }}
                            >
                              <Icon
                                type="delete"
                                id={`remove-col-${itemUUID}-${columnIndex}`}
                                style={{ fontSize: '14px' }}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              />
                            </Popconfirm>
                        </Tooltip>
                      </div>
                    </>
                  )}
                </MoveableColumn>
                {!isRearranging && (<ResizableColumn
                  itemUUID={itemUUID}
                  uuid={column.uuid}
                  columnIndex={columnIndex}
                  onResize={onResize}
                  left={0}
                  leftStart={leftStart}
                  scaling={scaling}
                  onAddColumn={onAddColumn}
                  isResizable={columnIndex !== headers.length - 1}
                />)}
                </>
              );
            })}
            </PageElementsProvider>
            {editable && <DynamicTableDragLayer scaling={scaling} tableRef={tableRef} tableUUID={itemUUID} />}
          </DynamicTable>
        </CreativeBlockContainer>
        {bottomBlockVisible && (
          <ComplexBlockRenderer
            key={dynamicTableBlock.style.tableProperties?.bottomBlock}
            itemUUID={dynamicTableBlock.style.tableProperties?.bottomBlock}
            editable={editable}
            parentUUID={parentUUID}
            getElementStyles={getElementStyles}
            scaling={scaling}
            {...rest}
            onClickBlockSelection= {() => onClickBlockSelection({itemUUID, parentUUID}) }
          />
        )}
      </div>
      {currentBlock && ReactDOM.createPortal(
        <PageElementsProvider>
          <DynamicTableSettings
            selectedColumn={selectedHeader}
            itemUUID={itemUUID}
            parentUUID={parentUUID}
            onClose={onClose}
            selectCellFormat={true}
            toggleSerialNumColumn={toggleSerialNumColumn}
          />
        </PageElementsProvider>,
        document.getElementById('sidebar-absolute-container'),
      )}
      {dynamicTableBlock.style.tableProperties?.showGroupHeaderContent && (
        <ResizeObserverForGroupHeader
          tableUUID={itemUUID}
          resizeCallback={resizeCallback}
          useStore={useStore}
          customName={`DynamicTable-${itemUUID}`}
        />
      )}
      {groupMenuPosition && (
        <div
          style={{
            position: 'absolute',
            left: (groupMenuPosition.x - (containerRect ? containerRect.left : 0)) / scaling,
            top: (groupMenuPosition.y - (containerRect ? containerRect.top : 0)) / scaling,
            height: 50,
            width: 50,
            background: 'white',
            zIndex: 2
          }}
        >
          <MenuListWrapper>
            <MenuListItem
              disabled={selectedGroupHeaders.length === 1}
              onClick={mergeCells}
            >
              Merge
            </MenuListItem>
            <MenuListItem
              onClick={splitCells}
            >
              Split
            </MenuListItem>
          </MenuListWrapper>
        </div>
      )}
    </>
  );
}

export default DynamicTableDesign;